import React, { useState, useEffect, useCallback } from 'react';
import { getTermineListePaginated, getAllFachberaterForTermine } from '../api';

function TerminListe({ user }) {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // PAGINATION STATE
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalTermine, setTotalTermine] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  
  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'datum',
    sortOrder: 'desc',
    fachberater: '' // Neuer Fachberater-Filter
  });

  // Verfügbare Fachberater
  const [availableFachberater, setAvailableFachberater] = useState([]);

  // fetchTermine als useCallback definieren
  const fetchTermine = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Filter-Parameter für Backend vorbereiten
      let filterParams = {};
      
      // Namenssuche (serverseitig)
      if (filters.search && filters.search.trim() !== '') {
        filterParams.search = filters.search.trim();
      }
      
      // Sortier-Parameter an das Backend übergeben
      filterParams.sortBy = filters.sortBy;
      filterParams.sortOrder = filters.sortOrder;
      
      // Fachberater-Filter
      if (filters.fachberater && filters.fachberater.trim() !== '') {
        filterParams.fachberater = filters.fachberater.trim();
      }
      
      const data = await getTermineListePaginated(
        user?.id, 
        user?.organization_id, 
        limit, 
        page, 
        filterParams
      );
      
      if (data.success) {
        setTermine(data.termine);
        setTotalTermine(data.total || 0);
        setTotalPages(data.totalPages || 0);
        setHasNextPage(data.hasNextPage || false);
        setHasPrevPage(data.hasPrevPage || false);
      } else {
        setError(data.error || 'Fehler beim Abrufen der Termine');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.organization_id, filters, limit, page]);

  // Termine laden, wenn Filter sich ändern
  useEffect(() => {
    fetchTermine();
  }, [filters, fetchTermine]);

  // Verfügbare Fachberater laden
  const loadFachberater = useCallback(async () => {
    try {
      const data = await getAllFachberaterForTermine(user?.id, user?.organization_id);
      if (data.success) {
        setAvailableFachberater(data.fachberater || []);
      } else {
        console.error('Fehler beim Laden der Fachberater:', data.error);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Fachberater:', err);
    }
  }, [user?.id, user?.organization_id]);

  // Fachberater beim ersten Laden abrufen
  useEffect(() => {
    loadFachberater();
  }, [loadFachberater]);

  // Wenn Filter sich ändern, Seite zurück auf 1
  useEffect(() => {
    setPage(1);
  }, [filters, limit]);

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Filter zurücksetzen
  const resetFilters = () => {
    setFilters({
      search: '',
      sortBy: 'datum',
      sortOrder: 'desc',
      fachberater: ''
    });
  };

  // Suchfeld leeren
  const clearSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: ''
    }));
  };

  // Paginierung-Funktionen
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage) {
      setPage(prev => prev - 1);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'bestätigt':
      case 'confirmed':
        return '#10b981';
      case 'ausstehend':
      case 'pending':
        return '#f59e0b';
      case 'abgesagt':
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const time = timeString || '';
      const formattedDate = date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return `${formattedDate} ${time}`;
    } catch {
      return dateString;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '-';
    
    try {
      const mins = parseInt(minutes);
      if (isNaN(mins)) return '-';
      
      const hours = Math.floor(mins / 60);
      const remainingMinutes = mins % 60;
      
      if (hours > 0 && remainingMinutes > 0) {
        return `${hours}h ${remainingMinutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else {
        return `${remainingMinutes}m`;
      }
    } catch {
      return minutes;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Lade Termine...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
        <div>Fehler: {error}</div>
        <button 
          onClick={fetchTermine}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            backgroundColor: '#4a6de5',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="termin-liste-page">
      <div className="termin-header">
        <div className="header-title">
          <span className="header-icon">📅</span>
          <div>
            <h2>Termin Liste</h2>
            <p className="header-description">Übersicht aller Termine in Ihrer Organisation</p>
          </div>
        </div>
        
        <div className="termin-count">
          <span className="count-number">{termine.length}</span>
          <span className="count-label">{termine.length === 1 ? 'Termin' : 'Termine'}</span>
          {filters.search && (
            <div className="search-indicator">
              <span className="search-text">🔍 "{filters.search}"</span>
            </div>
          )}
        </div>
      </div>

      {/* Filter-Bereich */}
      <div className="termin-filters">
        <div className="search-row">
          <div className="search-group search-group-flex">
            <label style={{ marginRight: 12 }}>🔍 Suche:</label>
            <div className="search-input-flex">
              <input
                type="text"
                placeholder="Suchen nach Kunde, Produkt, Stadt, Termin Art oder Fachberater..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="search-input"
              />
              {filters.search && (
                <button 
                  className="clear-search-button"
                  onClick={clearSearch}
                  title="Suche löschen"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {filters.search && (
            <div className="search-results-info">
              {termine.length === 0 ? (
                <span className="no-results">Keine Ergebnisse für "{filters.search}"</span>
              ) : (
                <span className="results-count">
                  {termine.length} Ergebnis{termine.length !== 1 ? 'se' : ''} für "{filters.search}"
                </span>
              )}
            </div>
          )}
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Sortieren nach:</label>
            <select 
              value={filters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="sort-select"
            >
              <option value="datum">Datum</option>
              <option value="kunde">Kunde</option>
              <option value="produkt">Produkt</option>
              <option value="status_wp">Status WP</option>
              <option value="status_pv">Status PV</option>
              <option value="fachberater">Fachberater</option>
              <option value="stadt">Stadt</option>
              <option value="termin_art">Termin Art</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Reihenfolge:</label>
            <select 
              value={filters.sortOrder} 
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="desc">Absteigend</option>
              <option value="asc">Aufsteigend</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Fachberater:</label>
            <select 
              value={filters.fachberater}
              onChange={(e) => handleFilterChange('fachberater', e.target.value)}
            >
              <option value="">Alle Fachberater</option>
              {availableFachberater.map(fachberater => (
                <option key={fachberater} value={fachberater}>
                  {fachberater}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <button className="reset-filters-button" onClick={resetFilters}>
              Alle Filter zurücksetzen
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Termine werden geladen...</div>
      ) : termine.length === 0 ? (
        <div className="no-data">
          {filters.search ? (
            <div>
              <p>Keine Termine mit der Suche "<strong>{filters.search}</strong>" gefunden.</p>
              <button onClick={clearSearch} className="clear-search-suggestion">
                Suche löschen und alle Termine anzeigen
              </button>
            </div>
          ) : (
            'Keine Termine gefunden.'
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="termin-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Kunde</th>
                <th>Produkt</th>
                <th>Status WP</th>
                <th>Status PV</th>
                <th>Kommentar</th>
                <th>Fachberater</th>
                <th>Straße</th>
                <th>Hausnummer</th>
                <th>Postleitzahl</th>
                <th>Stadt</th>
                <th>Termin Art</th>
                <th>Termin Tag</th>
                <th>Uhrzeit</th>
                <th>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {termine.map((termin, index) => (
                <tr 
                  key={termin.id || index}
                  className={`${filters.search && 
                    (termin.kunde?.toLowerCase().includes(filters.search.toLowerCase()) ||
                     termin.produkt?.toLowerCase().includes(filters.search.toLowerCase()) ||
                     termin.stadt?.toLowerCase().includes(filters.search.toLowerCase()) ||
                     termin.termin_art?.toLowerCase().includes(filters.search.toLowerCase()) ||
                     termin.fachberater?.toLowerCase().includes(filters.search.toLowerCase())) ? 'search-highlight' : ''}`}
                >
                  <td>{formatDate(termin.datum)}</td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.kunde || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.produkt || '-'}
                    </div>
                  </td>
                  <td>
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '10px',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(termin.status_wp) + '20',
                      color: getStatusColor(termin.status_wp)
                    }}>
                      {termin.status_wp || '-'}
                    </span>
                  </td>
                  <td>
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '10px',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(termin.status_pv) + '20',
                      color: getStatusColor(termin.status_pv)
                    }}>
                      {termin.status_pv || '-'}
                    </span>
                  </td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.kommentar || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.fachberater || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.strasse || '-'}
                    </div>
                  </td>
                  <td>{termin.hausnummer || '-'}</td>
                  <td>{termin.postleitzahl || '-'}</td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.stadt || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="termin-cell-content">
                      {termin.termin_art || '-'}
                    </div>
                  </td>
                  <td>{formatDate(termin.termin_tag)}</td>
                  <td>{termin.uhrzeit || '-'}</td>
                                     <td>{formatDuration(termin.dauer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginierung */}
      {!loading && termine.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Seite {page} von {totalPages}</span>
            <span>•</span>
            <span>{totalTermine} Termine insgesamt</span>
          </div>
          
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={goToPrevPage}
              disabled={!hasPrevPage}
            >
              ← Zurück
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`page-number ${page === pageNum ? 'active' : ''}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="pagination-btn" 
              onClick={goToNextPage}
              disabled={!hasNextPage}
            >
              Weiter →
            </button>
          </div>
          
          <div className="termine-per-page">
            <label>Termine pro Seite:</label>
            <select 
              value={limit} 
              onChange={(e) => setLimit(parseInt(e.target.value))}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .termin-liste-page {
          padding: 20px;
          max-width: 100%;
          overflow: hidden;
        }

        .termin-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }

        .header-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-icon {
          font-size: 2rem;
        }

        .header-title h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.5rem;
        }

        .header-description {
          margin: 4px 0 0 0;
          color: #718096;
          font-size: 0.875rem;
        }

        .termin-count {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: #f7fafc;
          padding: 12px 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .count-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #4a6de5;
        }

        .count-label {
          font-size: 0.75rem;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .search-indicator {
          margin-top: 8px;
          font-size: 0.7rem;
          color: #4a6de5;
          background: #ebf4ff;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .search-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 16px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .search-group-flex {
          display: flex;
          flex-direction: row;
          align-items: center;
          min-width: 0;
          flex: 1;
        }

        .search-input-flex {
          display: flex;
          flex-direction: row;
          align-items: center;
          flex: 1;
          min-width: 0;
          gap: 8px;
          max-width: 400px;
          width: 100%;
        }

        .search-input {
          flex: 1 1 0;
          min-width: 0;
          width: auto;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .clear-search-button {
          background: none;
          border: none;
          color: #a0aec0;
          cursor: pointer;
          font-size: 1.1rem;
          width: 32px;
          height: 32px;
          min-width: 32px;
          min-height: 32px;
          max-width: 32px;
          max-height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, color 0.2s;
          margin-left: 0;
        }

        .clear-search-button:hover {
          background-color: #f7fafc;
          color: #e53e3e;
        }

        .search-results-info {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
        }

        .no-results {
          color: #e53e3e;
          font-weight: 500;
        }

        .results-count {
          color: #38a169;
          font-weight: 500;
        }

        .clear-search-suggestion {
          background: #4a6de5;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          margin-top: 8px;
          transition: background-color 0.2s;
        }

        .clear-search-suggestion:hover {
          background: #3c5ce0;
        }

        .search-highlight {
          background-color: #fff5f5 !important;
        }

        .search-highlight:hover {
          background-color: #fed7d7 !important;
        }

        .termin-filters {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 20px;
          margin-bottom: 20px;
        }

        .filter-row {
          display: flex;
          gap: 20px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          min-width: 150px;
          flex: 1;
        }

        .filter-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4a5568;
          margin-bottom: 6px;
        }

        .filter-group select,
        .filter-group input {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: border-color 0.2s;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .reset-filters-button {
          padding: 8px 16px;
          background-color: #f3f4f6;
          color: #4b5563;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          margin-top: 22px;
          transition: all 0.2s;
        }

        .reset-filters-button:hover {
          background-color: #e5e7eb;
          color: #1f2937;
        }

                 .termin-table {
           width: 100%;
           border-collapse: collapse;
           background: white;
           border-radius: 8px;
           box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
           table-layout: auto;
           min-width: 1200px;
         }

        .table-container {
          position: relative;
          width: 100%;
          overflow-x: auto;
          overflow-y: auto;
          max-height: calc(100vh - 300px);
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .table-container::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }

        .table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }

        .table-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 6px;
        }

        .table-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .termin-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .termin-table thead th {
          position: sticky;
          top: 0;
          background: #f7fafc;
          z-index: 2;
        }

        .termin-table th {
          background: #f7fafc;
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          color: #4a5568;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.75rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .termin-table td {
          padding: 8px 12px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
          font-size: 0.75rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

                 .termin-cell-content {
           overflow: hidden;
           text-overflow: ellipsis;
           white-space: nowrap;
           max-width: 150px;
         }

         /* Spezifische Spaltenbreiten für bessere Lesbarkeit */
         .termin-table th:nth-child(8), /* Straße */
         .termin-table td:nth-child(8) {
           width: 250px;
           min-width: 200px;
         }

         .termin-table th:nth-child(1), /* Datum */
         .termin-table td:nth-child(1) {
           width: 100px;
           min-width: 100px;
         }

         .termin-table th:nth-child(2), /* Kunde */
         .termin-table td:nth-child(2) {
           width: 180px;
           min-width: 150px;
         }

         .termin-table th:nth-child(3), /* Produkt */
         .termin-table td:nth-child(3) {
           width: 100px;
           min-width: 80px;
         }

         .termin-table th:nth-child(4), /* Status WP */
         .termin-table td:nth-child(4) {
           width: 120px;
           min-width: 100px;
         }

         .termin-table th:nth-child(5), /* Status PV */
         .termin-table td:nth-child(5) {
           width: 120px;
           min-width: 100px;
         }

         .termin-table th:nth-child(6), /* Kommentar */
         .termin-table td:nth-child(6) {
           width: 200px;
           min-width: 150px;
         }

         .termin-table th:nth-child(7), /* Fachberater */
         .termin-table td:nth-child(7) {
           width: 150px;
           min-width: 120px;
         }

         .termin-table th:nth-child(9), /* Hausnummer */
         .termin-table td:nth-child(9) {
           width: 80px;
           min-width: 60px;
         }

         .termin-table th:nth-child(10), /* Postleitzahl */
         .termin-table td:nth-child(10) {
           width: 100px;
           min-width: 80px;
         }

         .termin-table th:nth-child(11), /* Stadt */
         .termin-table td:nth-child(11) {
           width: 200px;
           min-width: 150px;
         }

         .termin-table th:nth-child(12), /* Termin Art */
         .termin-table td:nth-child(12) {
           width: 120px;
           min-width: 100px;
         }

         .termin-table th:nth-child(13), /* Termin Tag */
         .termin-table td:nth-child(13) {
           width: 100px;
           min-width: 80px;
         }

         .termin-table th:nth-child(14), /* Uhrzeit */
         .termin-table td:nth-child(14) {
           width: 80px;
           min-width: 60px;
         }

         .termin-table th:nth-child(15), /* Dauer */
         .termin-table td:nth-child(15) {
           width: 80px;
           min-width: 60px;
         }

        .loading {
          text-align: center;
          padding: 40px;
          color: #718096;
          font-size: 1rem;
        }

        .error-message {
          color: #e53e3e;
          background-color: #fed7d7;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 16px;
          text-align: center;
        }

        .no-data {
          text-align: center;
          padding: 40px;
          color: #a0aec0;
          font-size: 1rem;
        }

        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 20px;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          color: #4a5568;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .pagination-btn {
          padding: 8px 16px;
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #3c5ce0;
        }

        .pagination-btn:disabled {
          background: #cbd5e0;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          gap: 4px;
        }

        .page-number {
          padding: 8px 12px;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #2d3748;
          transition: all 0.2s;
        }

        .page-number:hover {
          background: #edf2f7;
          border-color: #cbd5e0;
        }

        .page-number.active {
          background: #4a6de5;
          color: white;
          border-color: #4a6de5;
        }

        .termine-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .termine-per-page label {
          font-size: 0.875rem;
          color: #4a5568;
        }

        .termine-per-page select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
        }

        @media (max-width: 768px) {
          .termin-header {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .search-row {
            flex-direction: column;
            gap: 12px;
          }

          .search-group-flex {
            min-width: auto;
          }

          .filter-row {
            flex-direction: column;
            gap: 12px;
          }

          .filter-group {
            min-width: auto;
          }

          .pagination-container {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .pagination-controls {
            order: 1;
          }

          .pagination-info {
            order: 2;
          }

          .termine-per-page {
            order: 3;
          }
        }
      `}</style>
    </div>
  );
}

export default TerminListe; 